<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="$t('feedback.title')"
    width="550px"
    :close-on-click-modal="false"
    @close="handleClose"
    custom-class="feedback-dialog"
  >
    <div class="custom-textarea-wrapper">
      <textarea
        class="custom-textarea"
        v-model="feedbackText"
        :placeholder="$t('feedback.placeholder')"
        maxlength="500"
      ></textarea>
      <div class="custom-textarea-footer">
        <div class="upload-icon-wrapper" @click="triggerUpload">
          <i class="el-icon-picture-outline"></i>
        </div>
        <span class="word-count">{{ wordCountDisplay }}</span>
      </div>
    </div>

    <div class="upload-wrapper" :class="{ 'is-tif-file': isTifFile }">
      <el-upload
        class="feedback-uploader"
        ref="upload"
        action=""
        :auto-upload="false"
        list-type="picture-card"
        :on-change="handleChange"
        :on-remove="handleRemove"
        :on-exceed="handleExceed"
        accept=".jpg,.jpeg,.png,.bmp,.tiff,.tif"
        :limit="1"
        :file-list="fileList"
      >
      </el-upload>
      <!-- TIF 转换时的 loading 遮罩 -->
      <div v-if="isConvertingTif" class="tif-loading-overlay">
        <i class="el-icon-loading"></i>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <div class="feedback-usage-hint">
        {{ $t("feedback.feedbackVolume") }}{{ feedbackCount }} /
        {{ feedbackMax }}
      </div>

      <div class="footer-buttons">
        <el-button
          type="primary"
          @click="handleSubmit"
          :loading="isSubmitting"
          >{{ $t("feedback.submit") }}</el-button
        >
        <el-button @click="handleClose">{{ $t("feedback.cancel") }}</el-button>
      </div>
    </span>
  </el-dialog>
</template>

<script>
import { submitFeedback, getUserInfo } from "@/api";
import UTIF from "utif";

export default {
  name: "FeedbackModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      feedbackText: "",
      isSubmitting: false,
      fileList: [],
      uploadFile: null,
      feedbackCount: 0,
      feedbackMax: 0,
      previewUrl: null,
      isConvertingTif: false,
      loadingTimeoutId: null, // 保存 loading 显示的 setTimeout ID
      isTifFile: false, // 标识当前是否是 TIF 文件
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
    wordCountDisplay() {
      return `${this.feedbackText.length}/500`;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.fetchFeedbackCount();
      }
    },
  },
  methods: {
    async fetchFeedbackCount() {
      try {
        const response = await getUserInfo();
        if (response.success && response.data) {
          this.feedbackCount = response.data.feedback_count || 0;
          this.feedbackMax = response.data.feedback_max || 0;
        }
      } catch (error) {
        console.error("Failed to fetch feedback count:", error);
      }
    },

    handleClose() {
      this.feedbackText = "";
      this.isSubmitting = false;
      this.clearUpload();
      // 清理预览 URL，防止内存泄漏
      if (this.previewUrl) {
        // 如果是 Data URL，不需要 revokeObjectURL
        if (this.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(this.previewUrl);
        }
        this.previewUrl = null;
      }
      this.dialogVisible = false;
    },

    triggerUpload() {
      if (this.fileList.length > 0) {
        this.$notify.warning({
          title: this.$t("error.warningTitle"),
          message: this.$t("error.uploadLimit"),
        });
        return;
      }
      this.$refs.upload.$el.querySelector("input[type=file]").click();
    },

    handleChange(file, fileList) {
      const supportedTypes = [
        "image/jpeg",
        "image/png",
        "image/bmp",
        "image/tiff",
        "image/tif",
      ];

      // 检查文件扩展名，因为某些浏览器可能无法正确识别 tif 文件的 MIME 类型
      const fileName = file.name.toLowerCase();
      const isTifExtension =
        fileName.endsWith(".tif") || fileName.endsWith(".tiff");
      const isTypeValid =
        supportedTypes.includes(file.raw.type) || isTifExtension;
      const isSizeValid = file.raw.size / 1024 / 1024 < 50;

      if (!isTypeValid) {
        this.$notify.warning({
          title: this.$t("error.warningTitle"),
          message: this.$t("error.uploadTypeInvalid"),
        });
        this.clearUpload();
        return;
      }
      if (!isSizeValid) {
        this.$notify.warning({
          title: this.$t("error.warningTitle"),
          message: this.$t("error.uploadSizeExceed"),
        });
        this.clearUpload();
        return;
      }

      // 清理之前的预览 URL
      if (this.previewUrl && this.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }

      this.uploadFile = file.raw;

      // 为 tif 文件使用 tiff.js 转换为可预览的格式，因为浏览器无法直接显示 TIF
      if (isTifExtension) {
        // 标记为 TIF 文件，用于禁用滑动动画
        this.isTifFile = true;
        // 先设置 fileList，并创建一个占位符预览图，避免转换期间的空白
        const placeholderUrl = this.createPlaceholderImage();
        file.url = placeholderUrl;
        this.fileList = fileList.slice(-1);
        this.$nextTick(() => {
          if (this.fileList.length > 0 && this.fileList[0].uid === file.uid) {
            this.$set(this.fileList[0], "url", placeholderUrl);
          }
          // 清除之前的 timeout（如果有）
          if (this.loadingTimeoutId) {
            clearTimeout(this.loadingTimeoutId);
            this.loadingTimeoutId = null;
          }
          // 立即显示 loading，不延迟（因为转换可能很快，延迟会导致 loading 不显示）
          // 使用 $nextTick 确保在 DOM 更新后再显示，避免与滑动动画重叠
          this.$nextTick(() => {
            this.isConvertingTif = true; // 立即显示 loading
          });
          
          // 异步转换 TIF，转换完成后会替换占位符
          // loading 的隐藏已经在 convertTifToPreview 方法内部处理
          this.convertTifToPreview(file).catch(() => {
            // 错误已经在 convertTifToPreview 内部处理，这里只是确保不抛出未捕获的错误
          });
        });
      } else {
        // 标记为非 TIF 文件
        this.isTifFile = false;
        // 为非 TIF 文件创建预览 URL
        const previewUrl = URL.createObjectURL(file.raw);
        file.url = previewUrl;
        this.previewUrl = previewUrl;
        // 更新 fileList，确保包含预览 URL
        this.fileList = fileList.slice(-1);
        this.$nextTick(() => {
          if (this.fileList.length > 0 && this.fileList[0].uid === file.uid) {
            this.$set(this.fileList[0], "url", previewUrl);
          }
        });
      }
    },

    createPlaceholderImage() {
      // 创建一个简洁的占位符图片
      const canvas = document.createElement("canvas");
      canvas.width = 148; // Element UI picture-card 的默认尺寸
      canvas.height = 148;
      const ctx = canvas.getContext("2d");

      // 绘制背景
      ctx.fillStyle = "#f5f7fa";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 绘制边框
      ctx.strokeStyle = "#dcdfe6";
      ctx.lineWidth = 1;
      ctx.strokeRect(0, 0, canvas.width, canvas.height);

      return canvas.toDataURL("image/png");
    },

    convertTifToPreview(file) {
      return new Promise((resolve, reject) => {
        try {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              console.log("开始转换 TIF 文件...");
              const buffer = e.target.result;
              console.log("文件读取完成，buffer 大小:", buffer.byteLength);

              // 使用 utif 解码 TIF 文件
              const ifds = UTIF.decode(buffer);
              console.log("TIF 解码成功，找到", ifds.length, "个图像帧");

              if (ifds.length === 0) {
                throw new Error("TIF 文件中没有找到图像数据");
              }

              // 解码第一个图像帧
              UTIF.decodeImage(buffer, ifds[0]);
              const width = ifds[0].width;
              const height = ifds[0].height;
              console.log("图像尺寸:", width, "x", height);

              // 创建 canvas
              const canvas = document.createElement("canvas");
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext("2d");

              // 将解码后的图像数据转换为 ImageData
              const imageData = ctx.createImageData(width, height);
              const rgba = UTIF.toRGBA8(ifds[0]);
              imageData.data.set(rgba);
              ctx.putImageData(imageData, 0, 0);

              console.log("Canvas 绘制完成");

              // 将 canvas 转换为 PNG Data URL
              const dataUrl = canvas.toDataURL("image/png");
              console.log("Data URL 生成成功，长度:", dataUrl.length);

              // 更新 file 对象的 url
              file.url = dataUrl;
              this.previewUrl = dataUrl;

              // 更新 fileList
              this.$nextTick(() => {
                if (this.fileList.length > 0) {
                  const targetFile = this.fileList[0];
                  if (targetFile && targetFile.uid === file.uid) {
                    // 使用 Vue.set 确保响应式更新
                    this.$set(targetFile, "url", dataUrl);
                    // 同时更新 file 对象本身
                    this.$set(file, "url", dataUrl);
                    console.log("fileList 已更新，预览 URL 已设置");
                    // 清除可能还在等待的 loading 显示 timeout
                    if (this.loadingTimeoutId) {
                      clearTimeout(this.loadingTimeoutId);
                      this.loadingTimeoutId = null;
                    }
                    // 立即隐藏 loading，使用 $set 确保响应式更新
                    this.$set(this, 'isConvertingTif', false);
                    console.log("isConvertingTif 已设置为 false，当前值:", this.isConvertingTif);
                    // 强制更新视图，确保 loading 状态和预览图都更新
                    this.$forceUpdate();
                    resolve(dataUrl);
                  } else {
                    console.warn("文件 UID 不匹配，无法更新预览");
                    this.isConvertingTif = false;
                    this.$forceUpdate();
                    reject(new Error("文件 UID 不匹配"));
                  }
                } else {
                  console.warn("fileList 为空，无法更新预览");
                  this.isConvertingTif = false;
                  this.$forceUpdate();
                  reject(new Error("fileList 为空"));
                }
              });
            } catch (error) {
              console.error("TIF 转换失败:", error);
              // 清除可能还在等待的 loading 显示 timeout
              if (this.loadingTimeoutId) {
                clearTimeout(this.loadingTimeoutId);
                this.loadingTimeoutId = null;
              }
              this.isConvertingTif = false;
              reject(error);
            }
          };
          reader.onerror = (error) => {
            console.error("文件读取失败:", error);
            // 清除可能还在等待的 loading 显示 timeout
            if (this.loadingTimeoutId) {
              clearTimeout(this.loadingTimeoutId);
              this.loadingTimeoutId = null;
            }
            this.isConvertingTif = false;
            reject(new Error("无法读取 TIF 文件"));
          };
          reader.readAsArrayBuffer(file.raw);
        } catch (error) {
          console.error("处理 TIF 文件时出错:", error);
          // 清除可能还在等待的 loading 显示 timeout
          if (this.loadingTimeoutId) {
            clearTimeout(this.loadingTimeoutId);
            this.loadingTimeoutId = null;
          }
          this.isConvertingTif = false;
          reject(error);
        }
      }).catch((error) => {
        console.error("TIF 预览转换失败:", error);
        // 清除可能还在等待的 loading 显示 timeout
        if (this.loadingTimeoutId) {
          clearTimeout(this.loadingTimeoutId);
          this.loadingTimeoutId = null;
        }
        this.isConvertingTif = false; // 确保隐藏 loading
        this.$notify.warning({
          title: this.$t("error.warningTitle"),
          message:
            this.$t("error.uploadTypeInvalid") ||
            "无法预览此 TIF 文件: " + (error.message || "未知错误"),
        });
        this.clearUpload();
        // 重新抛出错误，让外部的 catch 也能处理
        throw error;
      });
    },

    handleRemove() {
      // 清理预览 URL，防止内存泄漏
      if (this.previewUrl) {
        // 如果是 Data URL，不需要 revokeObjectURL
        if (this.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(this.previewUrl);
        }
        this.previewUrl = null;
      }
      // 清除可能还在等待的 loading 显示 timeout
      if (this.loadingTimeoutId) {
        clearTimeout(this.loadingTimeoutId);
        this.loadingTimeoutId = null;
      }
      this.uploadFile = null;
      this.fileList = [];
      this.isConvertingTif = false; // 重置 loading 状态
      this.isTifFile = false; // 重置 TIF 文件标识
    },

    handleExceed() {
      this.$notify.warning({
        title: this.$t("error.warningTitle"),
        message: this.$t("error.uploadLimit"),
      });
    },

    clearUpload() {
      // 清除可能还在等待的 loading 显示 timeout
      if (this.loadingTimeoutId) {
        clearTimeout(this.loadingTimeoutId);
        this.loadingTimeoutId = null;
      }
      this.uploadFile = null;
      this.fileList = [];
      this.isConvertingTif = false; // 重置 loading 状态
      this.isTifFile = false; // 重置 TIF 文件标识
      if (this.$refs.upload) {
        this.$refs.upload.clearFiles();
      }
    },

    async handleSubmit() {
      if (!this.feedbackText.trim()) {
        this.$notify.warning({
          title: this.$t("error.warningTitle"),
          message: this.$t("error.2003"),
        });
        return;
      }

      const formData = new FormData();
      formData.append("content", this.feedbackText);

      // 获取文件大小，用于动态调整超时时间
      const fileSize = this.uploadFile ? this.uploadFile.size : 0;

      if (this.uploadFile) {
        formData.append("image", this.uploadFile);
      }

      this.isSubmitting = true;
      try {
        await submitFeedback(formData, fileSize);
        this.$notify.success({
          title: this.$t("success.successTitle"),
          message: this.$t("success.submitSuccess"),
        });
        this.handleClose();
      } catch (error) {
        console.error("Feedback submission failed:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
::v-deep .el-dialog__header {
  border-bottom: 1px solid #ebeef5;
  padding: 15px 20px;
}
::v-deep .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
}
::v-deep .el-dialog__body {
  padding: 20px;
}

.custom-textarea-wrapper {
  background-color: #ebeef5;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 5px 15px;
  display: flex;
  flex-direction: column;
  height: 190px;
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.custom-textarea-wrapper:hover {
  border-color: #c0c4cc;
}
.custom-textarea-wrapper:focus-within {
  border-color: #409eff;
}
.custom-textarea {
  width: 100%;
  flex-grow: 1;
  border: none;
  outline: none;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  color: #606266;
  padding: 0;
  background-color: transparent;
}
.custom-textarea::placeholder {
  color: #aab2bd;
}
.custom-textarea-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 5px;
}
.upload-icon-wrapper {
  font-size: 22px;
  color: #626366;
  cursor: pointer;
  transition: color 0.2s;
}
.upload-icon-wrapper:hover {
  color: #909399;
}
.word-count {
  font-size: 12px;
  color: #909399;
}

/* --- 上传组件样式 --- */
.upload-wrapper {
  position: relative;
  margin-top: 10px;
  display: inline-block;
}

.feedback-uploader {
  margin-top: 0;
}
::v-deep .feedback-uploader .el-upload--picture-card {
  display: none;
}
::v-deep
  .feedback-uploader
  .el-upload-list--picture-card
  .el-upload-list__item {
  width: 80px;
  height: 80px;
  margin: 0;
}

/* 禁用 TIF 文件的预览滑动动画 */
.upload-wrapper.is-tif-file ::v-deep .el-upload-list--picture-card .el-upload-list__item {
  transition: none !important;
  animation: none !important;
}

.upload-wrapper.is-tif-file ::v-deep .el-upload-list--picture-card .el-upload-list__item-enter-active,
.upload-wrapper.is-tif-file ::v-deep .el-upload-list--picture-card .el-upload-list__item-leave-active {
  transition: none !important;
}

.upload-wrapper.is-tif-file ::v-deep .el-upload-list--picture-card .el-upload-list__item-enter,
.upload-wrapper.is-tif-file ::v-deep .el-upload-list--picture-card .el-upload-list__item-leave-to {
  transform: none !important;
  opacity: 1 !important;
}

/* TIF 转换时的 loading 遮罩 */
.tif-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 80px;
  height: 80px;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 6px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
}

.tif-loading-overlay .el-icon-loading {
  font-size: 20px;
  color: #409eff;
}

::v-deep .el-dialog__footer {
  border-top: 1px solid #ebeef5;
  padding: 15px 20px;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.feedback-usage-hint {
  font-size: 12px;
  color: #909399;
}

/* --- 移动端适配 --- */
@media (max-width: 768px) {
  ::v-deep .feedback-dialog {
    width: 90vw !important;
  }
  ::v-deep .el-dialog__body {
    padding: 15px;
  }

  .dialog-footer {
    flex-direction: column;
    gap: 15px;
  }

  .feedback-usage-hint {
    text-align: center;
  }

  .footer-buttons {
    display: flex;
    flex-direction: column-reverse;
    width: 100%;
    gap: 10px;
  }

  .footer-buttons .el-button {
    width: 100%;
    margin: 0 !important;
  }
}
</style>
